import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import { IconComponent } from '@reforgium/icon';
import { LangPipe, RouteWatcher } from '@reforgium/presentia';
import { SkSelectComponent } from '@ss/ui';

import { urls } from 'constants/urls.constant';

import { DocumentVersionDto } from 'entities/document.model';
import { DocumentService } from 'use-cases/document.service';
import { MovingGradientDirective } from 'features/panels/moving-gradient.directive';

@Component({
  selector: 'app-history-panel',
  template: `
    <div appMovingGradient class="panel-card history-panel">
      <div class="history-toolbar">
        <ng-content />

        @if (documentVersions().length) {
          <div class="version-control">
            <sk-select
              class="version-select"
              [clearable]="false"
              [options]="versionOptions()"
              optionLabel="label"
              optionValue="id"
              [(value)]="selectedVersionId"
              [placeholder]="'document.placeholders.revision' | lang"
              [ariaLabel]="'document.placeholders.revision' | lang"
              (changed)="navigateToView($event)"
            />
            @if (hasPerm()) {
              <button
                type="button"
                class="version-edit"
                [attr.aria-label]="'document.actions.edit-revision' | lang"
                (click)="navigateToEdit(versionId())"
              >
                <re-icon name="edit" width="20" height="20" appearance="info" />
              </button>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      max-width: 100%;
      min-width: 0;
    }
    .history-panel {
      align-self: start;
      height: auto;
      min-height: 0;
      padding: 16px;
      background: #1c3449;
      border-radius: 18px !important;
      background-image: none;
    }

    .history-toolbar,
    .version-control {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .history-toolbar {
      justify-content: space-between;
      flex-direction: row-reverse;
      flex-wrap: wrap;
    }

    .version-control {
      min-width: 0;
      margin-left: 0;
      flex: 1 1 180px;
      flex-wrap: nowrap;
    }

    .version-select {
      display: inline-flex;
      flex: 1 1 246px;
      width: 246px;
      min-width: 0;
    }

    .version-edit {
      display: grid;
      flex: 0 0 auto;
      place-items: center;
      width: var(--sk-input-height, 34px);
      height: var(--sk-input-height, 34px);
      padding: 0;
      border: 0;
      border-radius: var(--radius-md);
      background: var(--primary-50);
      cursor: pointer;
    }

    .version-edit:hover {
      background: var(--primary-100);
    }

    @media (max-width: 600px) {
      .history-toolbar {
        flex-direction: column-reverse;
        align-items: stretch;
      }
      .version-control {
        flex: 0 1 auto;
        align-items: stretch;
        width: 100%;
      }
      .version-select {
        flex: 1 1 auto;
        width: auto;
      }
    }
  `,
  imports: [IconComponent, LangPipe, SkSelectComponent, MovingGradientDirective],
})
export class HistoryPanel {
  hasPerm = input<boolean>(false);

  watcher = inject(RouteWatcher);
  documentService = inject(DocumentService);
  router = inject(Router);

  versionId = computed<number | undefined>(() => {
    const queryVersionId = this.watcher.query()['versionId'];

    return queryVersionId ? Number(queryVersionId) : this.documentService.publicDocumentVersion.value()?.id;
  });

  documentVersions = computed<DocumentVersionDto[]>(() => this.documentService.versions.value() ?? []);

  versionOptions = computed(() =>
    this.documentVersions().map((version) => ({
      ...version,
      label: this.formatVersionDate(version.versionDate),
    })),
  );

  selectedVersionId = signal<number | null>(null);

  constructor() {
    effect(() => {
      this.selectedVersionId.set(this.versionId() ?? null);
    });
  }

  navigateToEdit(versionId: number | undefined): void {
    if (!versionId) return;

    const { id } = this.watcher.params();

    void this.router.navigate([urls.documentEdit(id)], {
      queryParams: {
        versionId,
      },
    });
  }

  navigateToView(versionId: number | null): void {
    if (!versionId) return;

    void this.router.navigate([], {
      queryParams: {
        versionId,
      },
    });
  }

  private formatVersionDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU').format(new Date(value));
  }
}
